import React, { createContext, useContext, useEffect, useState } from 'react'

/**
 * Basic accordion context
 *
 * Accordion is mostly used to display FAQ items
 *
 * @see src/blocks/faq-page/faq-page-questions.tsx
 * @see src/blocks/how-it-works-sectons/how-it-works-sections.tsx
 * @see src/blocks/questions/questions.tsx
 *
 * @see https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/context/#basic-example
 */

export type AccordionIndexType = number | string | false

export type AccordionContextType = [
  AccordionIndexType,
  React.Dispatch<React.SetStateAction<AccordionIndexType>>
]

export const AccordionContext = createContext<AccordionContextType>(undefined!)

type Props = {
  children: React.ReactNode
  isOpen?: AccordionIndexType
  isActive?: boolean
}

export const AccordionProvider = ({
  children,
  isActive,
  isOpen = false,
}: Props) => {
  const state = useState(isOpen)

  /**
   * Add ability to force close accordion from "outside"
   *
   * You can pass boolean isActive to <Accordion>:
   * If stateIsOpen, and isActive changes to false, active item will be closed.
   *
   * @note that this is not the best solution, but it works for this case ;)
   */
  const [stateIsOpen, setIsOpen] = state
  useEffect(() => {
    if (typeof isActive === 'boolean' && stateIsOpen && !isActive) {
      setIsOpen(false)
    }
  }, [isActive, stateIsOpen, setIsOpen])

  return (
    <AccordionContext.Provider value={state}>
      {children}
    </AccordionContext.Provider>
  )
}

export const useAccordion = () => {
  const context = useContext(AccordionContext)
  if (!context) {
    throw new Error('Missing Accordion Context!')
  }
  return context
}
